import type { DashboardAttachment, DashboardParticipant, DashboardThread } from "./types.js";
import type { DashboardMessageContact } from "./messages.types.js";

const CURRENT_USER: DashboardParticipant = {
  id: "me",
  name: "Me",
  avatarLabel: "ME",
  accent: "sky",
  presence: "online",
  roleLabel: "Program Lead",
  isCurrentUser: true,
};

const JOHN_DOE: DashboardParticipant = {
  id: "john-doe",
  name: "John Doe",
  avatarLabel: "JD",
  accent: "emerald",
  presence: "online",
  roleLabel: "Client Engineer",
};

const SARAH_LEE: DashboardParticipant = {
  id: "sarah-lee",
  name: "Sarah Lee",
  avatarLabel: "SL",
  accent: "amber",
  presence: "away",
  roleLabel: "Design Reviewer",
};

const MAYA_PATEL: DashboardParticipant = {
  id: "maya-patel",
  name: "Maya Patel",
  avatarLabel: "MP",
  accent: "violet",
  presence: "offline",
  roleLabel: "Operations Manager",
};

const SARAH_JENKINS: DashboardParticipant = {
  id: "sarah-jenkins",
  name: "Sarah Jenkins",
  avatarLabel: "SJ",
  accent: "rose",
  presence: "online",
  roleLabel: "Client Coordinator",
};

const NEXA_TEAM: DashboardParticipant = {
  id: "nexa-team",
  name: "Nexa Robotics",
  avatarLabel: "NR",
  accent: "slate",
  presence: "offline",
  roleLabel: "Manufacturing Team",
};

export const DASHBOARD_MESSAGE_CONTACTS: DashboardMessageContact[] = [
  {
    id: JOHN_DOE.id,
    name: JOHN_DOE.name,
    email: "john.doe@acme.example",
    avatarLabel: JOHN_DOE.avatarLabel,
    accent: JOHN_DOE.accent,
    presence: JOHN_DOE.presence,
    roleLabel: "Client Engineer",
    companyLabel: "Acme Corp",
    existingThreadId: "acme",
  },
  {
    id: SARAH_LEE.id,
    name: SARAH_LEE.name,
    email: "sarah.lee@acme.example",
    avatarLabel: SARAH_LEE.avatarLabel,
    accent: SARAH_LEE.accent,
    presence: SARAH_LEE.presence,
    roleLabel: "Design Reviewer",
    companyLabel: "Acme Corp",
    existingThreadId: "acme",
  },
  {
    id: MAYA_PATEL.id,
    name: MAYA_PATEL.name,
    email: "maya.patel@globaltech.example",
    avatarLabel: MAYA_PATEL.avatarLabel,
    accent: MAYA_PATEL.accent,
    presence: MAYA_PATEL.presence,
    roleLabel: "Operations Manager",
    companyLabel: "Global Tech",
    existingThreadId: "global-tech",
  },
  {
    id: SARAH_JENKINS.id,
    name: SARAH_JENKINS.name,
    email: "sarah.jenkins@clients.example",
    avatarLabel: SARAH_JENKINS.avatarLabel,
    accent: SARAH_JENKINS.accent,
    presence: SARAH_JENKINS.presence,
    roleLabel: "Client Coordinator",
    companyLabel: "CAD Review",
    existingThreadId: "sarah",
  },
  {
    id: "marcus-vale",
    name: "Marcus Vale",
    email: "marcus.vale@nexa.example",
    avatarLabel: "MV",
    accent: "emerald",
    presence: "online",
    roleLabel: "Mechanical Engineer",
    companyLabel: "Nexa Robotics",
  },
  {
    id: "priya-nair",
    name: "Priya Nair",
    email: "priya.nair@ops.example",
    avatarLabel: "PN",
    accent: "violet",
    presence: "away",
    roleLabel: "Program Coordinator",
    companyLabel: "Industrial Hub",
  },
];

const ACME_ATTACHMENT: DashboardAttachment = {
  id: "perf-report",
  name: "Performance_Report_v2.pdf",
  kind: "pdf",
  sizeLabel: "1.2 MB",
  updatedAtLabel: "10:35 AM",
  metaLabel: "PDF document",
};

export const DASHBOARD_MESSAGE_THREADS: DashboardThread[] = [
  {
    id: "acme",
    title: "Acme Corp",
    contextLabel: "Project Alpha",
    snippet: "The 3D model update is ready for review...",
    updatedAtLabel: "10:32 AM",
    updatedAtIso: "2026-03-31T10:32:00-07:00",
    participantsLabel: "John Doe, Sarah Lee",
    participants: [JOHN_DOE, SARAH_LEE, CURRENT_USER],
    unreadCount: 2,
    isPinned: true,
    attachment: ACME_ATTACHMENT,
    messages: [
      {
        id: "acme-1",
        senderId: JOHN_DOE.id,
        sender: JOHN_DOE.name,
        sentAtLabel: "10:30 AM",
        sentAtIso: "2026-03-31T10:30:00-07:00",
        body: "The 3D model update is ready for review. I've adjusted the animation for the hydraulic assembly as requested.",
        direction: "inbound",
      },
      {
        id: "acme-2",
        senderId: CURRENT_USER.id,
        sender: CURRENT_USER.name,
        sentAtLabel: "10:32 AM",
        sentAtIso: "2026-03-31T10:32:00-07:00",
        body: "Great work, John. Checking it now. Did you manage to reduce the polygon count for mobile optimization?",
        direction: "outbound",
        status: "delivered",
      },
      {
        id: "acme-3",
        senderId: JOHN_DOE.id,
        sender: JOHN_DOE.name,
        sentAtLabel: "10:35 AM",
        sentAtIso: "2026-03-31T10:35:00-07:00",
        body: "Yes, reduced by 40%. Here is the performance report for the WebGL viewer.",
        direction: "inbound",
        attachment: ACME_ATTACHMENT,
        attachments: [ACME_ATTACHMENT],
      },
    ],
  },
  {
    id: "global-tech",
    title: "Global Tech",
    contextLabel: "Manual Rollout",
    snippet: "Can we schedule a demo for the new manual?",
    updatedAtLabel: "Yesterday",
    updatedAtIso: "2026-03-30T16:05:00-07:00",
    participantsLabel: "Maya Patel",
    participants: [MAYA_PATEL, CURRENT_USER],
    messages: [
      {
        id: "global-1",
        senderId: MAYA_PATEL.id,
        sender: MAYA_PATEL.name,
        sentAtLabel: "Yesterday",
        sentAtIso: "2026-03-30T16:05:00-07:00",
        body: "Can we schedule a demo for the new manual next week?",
        direction: "inbound",
      },
    ],
  },
  {
    id: "sarah",
    title: "Sarah Jenkins",
    contextLabel: "CAD Review",
    snippet: "I've uploaded the new CAD files to the assets folder.",
    updatedAtLabel: "Oct 24",
    updatedAtIso: "2025-10-24T09:20:00-07:00",
    participantsLabel: "Sarah Jenkins",
    participants: [SARAH_JENKINS, CURRENT_USER],
    unreadCount: 1,
    attachment: {
      id: "cad-overlay",
      name: "Bracket_Revision_C.dwg",
      kind: "cad",
      sizeLabel: "8.4 MB",
      updatedAtLabel: "Oct 24",
      metaLabel: "CAD drawing",
    },
    messages: [
      {
        id: "sarah-1",
        senderId: SARAH_JENKINS.id,
        sender: SARAH_JENKINS.name,
        sentAtLabel: "Oct 24",
        sentAtIso: "2025-10-24T09:20:00-07:00",
        body: "I've uploaded the new CAD files to the assets folder.",
        direction: "inbound",
        attachment: {
          id: "cad-overlay",
          name: "Bracket_Revision_C.dwg",
          kind: "cad",
          sizeLabel: "8.4 MB",
          updatedAtLabel: "Oct 24",
          metaLabel: "CAD drawing",
        },
        attachments: [
          {
            id: "cad-overlay",
            name: "Bracket_Revision_C.dwg",
            kind: "cad",
            sizeLabel: "8.4 MB",
            updatedAtLabel: "Oct 24",
            metaLabel: "CAD drawing",
          },
        ],
      },
    ],
  },
  {
    id: "nexa",
    title: "Nexa Robotics",
    contextLabel: "Assembly Review",
    snippet: "The assembly step #4 needs more detail on the bracket.",
    updatedAtLabel: "Oct 22",
    updatedAtIso: "2025-10-22T13:12:00-07:00",
    participantsLabel: "Nexa Robotics",
    participants: [NEXA_TEAM, CURRENT_USER],
    messages: [
      {
        id: "nexa-1",
        senderId: NEXA_TEAM.id,
        sender: NEXA_TEAM.name,
        sentAtLabel: "Oct 22",
        sentAtIso: "2025-10-22T13:12:00-07:00",
        body: "The assembly step #4 needs more detail on the bracket.",
        direction: "inbound",
      },
    ],
  },
];

export function getDefaultDashboardCurrentUser(): DashboardParticipant {
  return CURRENT_USER;
}
